import React, {
  useMemo,
  useState,
} from 'react'

import {
  Stack,
  Group,
  Button,
  Select,
  SegmentedControl,
  Card,
  Text,
  Badge,
  Tooltip,
  ActionIcon,
  Table,
  Center,
  Loader,
  Box,
  ScrollArea,
  Paper,
  Switch,
} from '@mantine/core'

import { notifications } from '@mantine/notifications'

import { useQueryClient } from '@tanstack/react-query'

import {
  IconCheck,
  IconPlus,
  IconEdit,
  IconTrash,
  IconCalendarWeek,
  IconTable,
  IconPlayerPlayFilled,
  IconPlayerPauseFilled,
} from '@tabler/icons-react'

import Device, {
  ValveKey,

} from '@data/device/model/device'

import ValveScheduleForm from './ValveScheduleForm'
import {
  describeValveSchedule,
  getValveScheduleBatchOutcome,
} from './valveScheduleBatch'

import { describeApiError } from '@common/utils/apiErrors'

import {
  useGetValveSchedule,
  useValveScheduleCreate,
  useValveScheduleDelete,
  useValveScheduleUpdate,
} from '@data/device/device'
import {
  DayOfWeek,
  ValveSchedule,
  ValveScheduleCreate,
} from '@data/device/model/valveSchedule'

interface ValveScheduleTabProps {
  device: Device
}

const DAYS_OF_WEEK: DayOfWeek[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

const VALVE_COLORS: Record<
  ValveKey,
  string
> = {
  V1: 'blue',
  V2: 'green',
  V3: 'violet',
  V4: 'orange',
  V5: 'pink',
}

const ValveScheduleTab: React.FC<
  ValveScheduleTabProps
> = ({ device }) => {
  const [viewMode, setViewMode] = useState<'day' | 'table'>('table')

  const [selectedValve, setSelectedValve] = useState<ValveKey | 'all'>('all')

  const [showForm, setShowForm] = useState(false)

  const [editingSchedule, setEditingSchedule] = useState<ValveSchedule | null>(null)

  const [prefilledDay, setPrefilledDay] = useState<DayOfWeek>()

  const [prefilledTime, setPrefilledTime] = useState<string>()

  const queryClient = useQueryClient()

  const {
    data: schedules = [],
    isPending,
    isFetching,
  } = useGetValveSchedule(
    device.dUuid,
  )

  const {
    mutateAsync: updateSchedule,
    isPending: isPendingUpdate,
  } = useValveScheduleUpdate()

  const {
    mutateAsync: createSchedule,
    isPending: isPendingCreate,
  } = useValveScheduleCreate()

  const {
    mutateAsync: deleteSchedule,
    isPending: isPendingDelete,
  } = useValveScheduleDelete()

  const mutationLoading =
    isPendingUpdate ||
    isPendingCreate ||
    isPendingDelete

  const valveOptions = useMemo(() => {
    const commissioned = Object.values(
      device.valves,
    ).filter(
      (v) => v.state !== 7,
    )

    return [
      {
        value: 'all',
        label: 'All Valves',
      },

      ...commissioned.map((v) => ({
        value: v.key,
        label: v.name
          ? `${v.key} - ${v.name}`
          : v.key,
      })),
    ]
  }, [device.valves])

  const filteredSchedules =
    useMemo(() => {
      if (selectedValve === 'all') {
        return schedules
      }

      return schedules.filter((s) => s.valveId === selectedValve)
    }, [schedules, selectedValve])

  const schedulesByDayAndHour =
    useMemo(() => {
      const grouped: Record<string, ValveSchedule[]> = {}

      filteredSchedules.forEach(
        (schedule) => {
          const hour = parseInt(
            schedule.timeOfDay.split(':',)[0], 10
          )

          const key = `${schedule.dayOfWeek}-${hour}`

          if (!grouped[key]) {
            grouped[key] = []
          }

          grouped[key].push(schedule)
        },
      )

      return grouped
    }, [filteredSchedules])

  const handleAddSchedule =
    () => {
      setEditingSchedule(null)
      setPrefilledDay(undefined)
      setPrefilledTime(undefined)
      setShowForm(true)
    }

  const handleEditSchedule = (schedule: ValveSchedule) => {
    setEditingSchedule(schedule)
    setShowForm(true)
  }

  // Sticky, matching the batch-create failure notification.
  const showScheduleError = (title: string, error: unknown) => {
    notifications.show({
      title,
      message: describeApiError(error) ?? 'Please try again.',
      color: 'red',
      autoClose: false,
    })
  }

  const handleSaveSchedule =
    async (
      schedules: ValveScheduleCreate[],
    ): Promise<ValveScheduleCreate[]> => {
      // EDIT MODE
      if (editingSchedule?.id) {
        try {
          await updateSchedule({
            ...schedules[0],
            id: editingSchedule.id,
          })
        } catch (error) {
          showScheduleError(`Failed to update ${describeValveSchedule(editingSchedule)}`, error)

          // Returned as failed so the form stays open for a retry.
          return schedules
        }

        return []
      }

      // CREATE MODE
      const results = await Promise.allSettled(
        schedules.map(
          (schedule) =>
            createSchedule(schedule),
        ),
      )

      // Each create's response replaced the cached list, and they can resolve out of order,
      // so the cache may hold an older snapshot. Refetch once the whole batch has settled.
      void queryClient.invalidateQueries({ queryKey: ['valveSchedule', device.dUuid] })

      const outcome = getValveScheduleBatchOutcome(schedules, results, describeApiError)

      if (outcome.status === 'success') {
        notifications.show({
          title: outcome.title,
          message: outcome.message,
          color: 'green',
          icon: <IconCheck size={16} />,
        })

        return []
      }

      // Sticky: the message lists every failed schedule, which can take longer to read
      // than Mantine's default auto-close allows.
      notifications.show({
        title: outcome.title,
        message: outcome.message,
        color: 'red',
        autoClose: false,
      })

      return outcome.failed
    }

  const handleToggleActive =
    async (
      schedule: ValveSchedule,
    ) => {
      try {
        await updateSchedule({
          ...schedule,
          active: !schedule.active,
        })
      } catch (error) {
        showScheduleError(
          `Failed to ${schedule.active ? 'disable' : 'enable'} ${describeValveSchedule(schedule)}`,
          error,
        )
      }
    }

  const handleDeleteSchedule =
    async (
      schedule: ValveSchedule,
    ) => {
      if (
        !window.confirm(
          `Delete schedule for ${schedule.valveId} on ${schedule.dayOfWeek} at ${schedule.timeOfDay}?`,
        )
      ) {
        return
      }

      try {
        await deleteSchedule({
          dUuid: schedule.dUuid,
          id: schedule.id,
        })
      } catch (error) {
        showScheduleError(`Failed to delete ${describeValveSchedule(schedule)}`, error)
      }
    }

  const renderCalendar =
    () => (
      <ScrollArea>
        <Box miw={1200}>
          <Table
            withTableBorder
            withColumnBorders
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={90}>
                  Time
                </Table.Th>

                {DAYS_OF_WEEK.map(
                  (day) => (
                    <Table.Th key={day}>
                      {day}
                    </Table.Th>
                  ),
                )}
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {Array.from(
                { length: 24 },
                (_, hour) => (
                  <Table.Tr key={hour}>
                    <Table.Td fw={700}>
                      {hour.toString().padStart(2, '0')}:00
                    </Table.Td>

                    {DAYS_OF_WEEK.map(
                      (day) => {
                        const key = `${day}-${hour}`
                        const cellSchedules = schedulesByDayAndHour[key] || []

                        return (
                          <Table.Td
                            key={key}
                            style={{
                              cursor: 'pointer',
                              minWidth: 120,
                              verticalAlign: 'top',
                            }}
                            onClick={() => {
                              setEditingSchedule(null)

                              setPrefilledDay(day)

                              setPrefilledTime(
                                hour.toString().padStart(2, '0',) + ':00'
                              )
                              setShowForm(true)
                            }}
                          >
                            <Stack gap={4}>
                              {cellSchedules.map(
                                (
                                  schedule,
                                ) => (
                                  <Tooltip
                                    key={schedule.id}
                                    multiline
                                    label={
                                      <Stack gap={2}>
                                        <Text size="xs">
                                          <strong>
                                            {schedule.valveId}
                                          </strong>{' '}
                                          -{' '}
                                          {schedule.valveCommand.toUpperCase()}
                                        </Text>

                                        <Text size="xs">
                                          {schedule.timeOfDay}
                                        </Text>

                                        <Text size="xs">
                                          {schedule.active ? 'Enabled' : 'Disabled'}
                                        </Text>

                                        {schedule.description && (
                                          <Text size="xs">
                                            {schedule.description}
                                          </Text>
                                        )}
                                      </Stack>
                                    }
                                  >
                                    <Box
                                      onClick={(
                                        e,
                                      ) => {
                                        e.stopPropagation()
                                        handleEditSchedule(schedule)
                                      }}
                                      style={{
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <Badge
                                        color={VALVE_COLORS[schedule.valveId]}
                                        fullWidth
                                        variant={schedule.active ? 'filled' : 'outline'}
                                      >
                                        <Group
                                          gap={4}
                                          justify="space-between"
                                          wrap="nowrap"
                                        >
                                          <Text size="xs">
                                            {schedule.valveId}
                                          </Text>

                                          {schedule.valveCommand ===
                                          'open' ? (
                                            <IconPlayerPlayFilled size={10} />
                                          ) : (
                                            <IconPlayerPauseFilled size={10} />
                                          )}
                                        </Group>
                                      </Badge>
                                    </Box>
                                  </Tooltip>
                                ),
                              )}
                            </Stack>
                          </Table.Td>
                        )
                      },
                    )}
                  </Table.Tr>
                ),
              )}
            </Table.Tbody>
          </Table>
        </Box>
      </ScrollArea>
    )

  const renderTable = () => {
    const sortedSchedules =
      [...filteredSchedules].sort((a, b) => {
        const dayOrder = DAYS_OF_WEEK.indexOf(a.dayOfWeek) - DAYS_OF_WEEK.indexOf(b.dayOfWeek)

        if (dayOrder !== 0) {
          return dayOrder
        }

        return a.timeOfDay.localeCompare(b.timeOfDay)
      })

    return (
      <Table
        striped
        highlightOnHover
        withTableBorder
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              Valve
            </Table.Th>

            <Table.Th>
              Day
            </Table.Th>

            <Table.Th>
              Time
            </Table.Th>

            <Table.Th>
              Command
            </Table.Th>

            <Table.Th>
              Description
            </Table.Th>

            <Table.Th w={120}>
              Status
            </Table.Th>

            <Table.Th w={120}>
              Actions
            </Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {sortedSchedules.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Center py="lg">
                  <Text c="dimmed">
                    No schedules configured
                  </Text>
                </Center>
              </Table.Td>
            </Table.Tr>
          ) : (
            sortedSchedules.map(
              (
                schedule,
              ) => (
                <Table.Tr key={schedule.id}>
                  <Table.Td>
                    <Badge
                      color={VALVE_COLORS[schedule.valveId]}
                    >
                      {schedule.valveId}
                    </Badge>
                  </Table.Td>

                  <Table.Td>
                    {schedule.dayOfWeek}
                  </Table.Td>

                  <Table.Td>
                    {schedule.timeOfDay}
                  </Table.Td>

                  <Table.Td>
                    <Badge
                      color={schedule.valveCommand === 'open' ? 'green' : 'red'}
                    >
                      {schedule.valveCommand.toUpperCase()}
                    </Badge>
                  </Table.Td>

                  <Table.Td>
                    {schedule.description || '-'}
                  </Table.Td>

                  <Table.Td>
                    <Switch
                      checked={schedule.active}
                      disabled={mutationLoading}
                      label={schedule.active ? 'Enabled' : 'Disabled'}
                      onChange={() =>
                        handleToggleActive(schedule)
                      }
                    />
                  </Table.Td>

                  <Table.Td>
                    <Group gap={4}>
                      <ActionIcon
                        variant="subtle"
                        onClick={() =>
                          handleEditSchedule(schedule)
                        }
                      >
                        <IconEdit size={16} />
                      </ActionIcon>

                      <ActionIcon
                        variant="subtle"
                        color="red"
                        loading={mutationLoading}
                        onClick={() =>
                          handleDeleteSchedule(schedule)
                        }
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ),
            )
          )}
        </Table.Tbody>
      </Table>
    )
  }

  // Initial load only. A background refetch (the post-batch invalidation, window focus)
  // must not swap the tab for a loader, or the open form unmounts and loses its state;
  // the form shows it on its submit button instead (isRefreshing).
  if (isPending) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    )
  }

  return (
    <Stack gap="md">
      <Card withBorder>
        <Group justify="space-between">
          <Text
            fw={700}
            size="lg"
          >
            Valve Schedule
            Configuration
          </Text>

          <Group>
            <Select
              w={220}
              data={valveOptions}
              value={selectedValve}
              onChange={(
                value,
              ) =>
                setSelectedValve(
                  value as | ValveKey | 'all',
                )
              }
            />

            <SegmentedControl
              value={viewMode}
              onChange={(
                value,
              ) =>
                setViewMode(
                  value as | 'day' | 'table'
                )
              }
              data={[
                {
                  value: 'day',
                  label: (
                    <Group
                      gap={4}
                      align="center"
                      wrap="nowrap"
                    >
                      <IconCalendarWeek size={14} />
                      <span>Day</span>
                    </Group>
                  ),
                },

                {
                  value: 'table',
                  label: (
                    <Group
                      gap={4}
                      align="center"
                      wrap="nowrap"
                    >
                      <IconTable size={14} />
                      <span>Table</span>
                    </Group>
                  ),
                },
              ]}
            />

            <Button
              leftSection={
                <IconPlus size={16} />
              }
              onClick={handleAddSchedule}
            >
              Add
            </Button>
          </Group>
        </Group>
      </Card>

      <Paper
        withBorder
        p="md"
      >
        {viewMode === 'day' ? renderCalendar() : renderTable()}
      </Paper>

      <ValveScheduleForm
        opened={showForm}
        onClose={() =>
          setShowForm(false)
        }
        onSave={handleSaveSchedule}
        isRefreshing={isFetching}
        device={device}
        editingSchedule={editingSchedule}
        existingSchedules={schedules}
        prefilledDay={prefilledDay}
        prefilledTime={prefilledTime}
      />
    </Stack>
  )
}

export default ValveScheduleTab